/**
 * ApprovalRequestPage — 결재 상신 (신규 결재 요청 작성).
 *
 * 【역할】
 * - 신규 결재 건 작성 + 결재선(Step) 설계 + 제출
 * - 7-role 승인 체계 중 상신 화면 (APPROVE/FINAL/DECIDE/AGREE/CC/DELEGATE/NOTIFY)
 *
 * 【주요 기능】
 * - 기본 정보 입력 (title / description / refDomain / refId)
 * - 결재 실행 유형 선택 (SERIAL / PARALLEL) — Radio
 * - 결재선 작성 (step 행 추가/삭제, seq 자동/수동)
 * - 각 step: role (7종) + assigneeId + assigneeName
 * - 제출 → 승인 대상자에게 통보
 *
 * 【사용 훅】
 * - useTransaction
 * - useLabel (t)
 * - useState / Form.useForm
 *
 * 【Backend RPC serviceName】
 * - approval/submit  — 결재 상신 (마스터 + 결재선 원자적 저장)
 *
 * 【DataSet 구조】
 * - 없음 (로컬 state steps[] 을 inds 로 직접 전송)
 *
 * 【사용처】 router/index.tsx — `{ path: 'approval/request' }`
 * 【포팅 원본】 react-fastapi 신규 화면 (7-role 승인 체계)
 */
import { useState } from 'react'
import { Button, Card, Form, Input, Select, Space, Table, Typography, message, Radio } from 'antd'
import { DeleteOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons'
import { useTransaction } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import PageContainer from '@/components/common/PageContainer'

const { Title } = Typography

interface StepLine {
  key: string
  seq: number
  role: string
  assigneeId: string
  assigneeName?: string
}

const ROLES = [
  { value: 'APPROVE', labelKey: 'LBL_ROLE_APPROVE', fallback: '결재' },
  { value: 'FINAL', labelKey: 'LBL_ROLE_FINAL', fallback: '전결' },
  { value: 'DECIDE', labelKey: 'LBL_ROLE_DECIDE', fallback: '의사결정' },
  { value: 'AGREE', labelKey: 'LBL_ROLE_AGREE', fallback: '합의' },
  { value: 'CC', labelKey: 'LBL_ROLE_CC', fallback: '참조' },
  { value: 'DELEGATE', labelKey: 'LBL_ROLE_DELEGATE', fallback: '위임' },
  { value: 'NOTIFY', labelKey: 'LBL_ROLE_NOTIFY', fallback: '통보' }
]

/**
 * ApprovalRequestPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function ApprovalRequestPage() {
  const { t } = useLabel()
  const { transaction } = useTransaction()
  const [form] = Form.useForm()
  const [execType, setExecType] = useState<'SERIAL' | 'PARALLEL'>('SERIAL')
  const [steps, setSteps] = useState<StepLine[]>([
    { key: '1', seq: 1, role: 'APPROVE', assigneeId: 'admin', assigneeName: 'Administrator' }
  ])

  const addStep = () => {
    const next = steps.length + 1
    setSteps([...steps, { key: String(Date.now()), seq: next, role: 'APPROVE', assigneeId: '' }])
  }

  const removeStep = (key: string) => {
    setSteps(steps.filter((s) => s.key !== key).map((s, i) => ({ ...s, seq: i + 1 })))
  }

  const updateStep = (key: string, patch: Partial<StepLine>) => {
    setSteps(steps.map((s) => (s.key === key ? { ...s, ...patch } : s)))
  }

  const submit = async () => {
    const values = await form.validateFields()
    if (!steps.some((s) => s.assigneeId) || steps.length === 0) {
      void message.warning(t('MSG_NO_STEPS', '결재선을 1개 이상 지정하세요.'))
      return
    }

    const res = await transaction({
      id: 'submit',
      service: 'approval/submit',
      action: 'save',
      inds: {
        ds_approval: {
          rows: [
            {
              _state: 'C',
              title: values.title,
              description: values.description,
              execType,
              refDomain: values.refDomain,
              refId: values.refId
            }
          ]
        },
        ds_step: {
          rows: steps.map((s) => ({
            _state: 'C',
            seq: s.seq,
            role: s.role,
            assigneeId: s.assigneeId,
            assigneeName: s.assigneeName
          }))
        }
      }
    })
    if (res.success) {
      void message.success(t('MSG_APPROVAL_SUBMITTED', '결재가 상신되었습니다.'))
      form.resetFields()
      setSteps([{ key: '1', seq: 1, role: 'APPROVE', assigneeId: 'admin' }])
    }
  }

  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <Title level={3} style={{ margin: 0, marginBottom: 12 }}>
        {t('LBL_APPROVAL_REQUEST', '결재 요청')}
      </Title>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Form form={form} layout="vertical">
          <Form.Item name="title" label={t('LBL_TITLE', '제목')} rules={[{ required: true }]}>
            <Input placeholder={t('PH_APPROVAL_TITLE', '결재 제목')} />
          </Form.Item>
          <Form.Item name="description" label={t('LBL_DESCRIPTION', '설명')}>
            <Input.TextArea rows={3} placeholder={t('PH_APPROVAL_DESC', '결재 사유')} />
          </Form.Item>
          <Form.Item label={t('LBL_EXEC_TYPE', '진행 방식')}>
            <Radio.Group value={execType} onChange={(e) => setExecType(e.target.value)}>
              <Radio.Button value="SERIAL">SERIAL ({t('LBL_SEQUENTIAL', '순차')})</Radio.Button>
              <Radio.Button value="PARALLEL">PARALLEL ({t('LBL_PARALLEL', '병렬')})</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="refDomain" label={t('LBL_REF_DOMAIN', '참조 도메인')}>
            <Input placeholder="experiment / model_registry / dataset ..." />
          </Form.Item>
          <Form.Item name="refId" label={t('LBL_REF_ID', '참조 ID')}>
            <Input placeholder="1234" />
          </Form.Item>
        </Form>
      </Card>

      <Card
        size="small"
        title={t('LBL_APPROVAL_LINE', '결재선')}
        extra={
          <Button icon={<PlusOutlined />} onClick={addStep} size="small">
            {t('BTN_ADD_STEP', '단계 추가')}
          </Button>
        }
      >
        <Table<StepLine>
          rowKey="key"
          size="small"
          pagination={false}
          dataSource={steps}
          columns={[
            { title: '#', dataIndex: 'seq', width: 50 },
            {
              title: t('LBL_ROLE', '역할'),
              dataIndex: 'role',
              width: 140,
              render: (_v, r) => (
                <Select
                  value={r.role}
                  size="small"
                  style={{ width: 120 }}
                  onChange={(v) => updateStep(r.key, { role: v })}
                  options={ROLES.map((x) => ({
                    value: x.value,
                    label: t(x.labelKey, x.fallback)
                  }))}
                />
              )
            },
            {
              title: t('LBL_ASSIGNEE', '담당자'),
              dataIndex: 'assigneeId',
              render: (_v, r) => (
                <Input
                  size="small"
                  value={r.assigneeId}
                  placeholder="loginId (예: admin, user01)"
                  onChange={(e) => updateStep(r.key, { assigneeId: e.target.value })}
                />
              )
            },
            {
              title: t('LBL_NAME', '이름'),
              dataIndex: 'assigneeName',
              render: (_v, r) => (
                <Input
                  size="small"
                  value={r.assigneeName}
                  onChange={(e) => updateStep(r.key, { assigneeName: e.target.value })}
                />
              )
            },
            {
              title: '',
              key: 'del',
              width: 50,
              render: (_v, r) => (
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeStep(r.key)}
                />
              )
            }
          ]}
        />
      </Card>

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Space>
          <Button onClick={() => form.resetFields()}>{t('BTN_RESET_DEFAULT', '초기화')}</Button>
          <Button type="primary" icon={<SendOutlined />} onClick={submit}>
            {t('BTN_SUBMIT_APPROVAL', '상신')}
          </Button>
        </Space>
      </div>
    </PageContainer>
  )
}
